import React, { useState, useEffect } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import { 
  ArrowLeft, Calendar, Download, FileText, CheckCircle, AlertCircle,
  Loader2, DollarSign, Clock, CreditCard
} from "lucide-react";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { usePortalAuth } from "./PortalLayout";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const getStatusColor = (status) => {
  const map = { paid: "#22C55E", sent: "#3B82F6", pending: "#F59E0B", overdue: "#EF4444", cancelled: "#94A3B8", draft: "#94A3B8" };
  return map[status] || "#94A3B8";
};

const formatDate = (dateStr) => {
  if (!dateStr) return "N/A";
  return new Date(dateStr).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
};

const PortalInvoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    const fetchInvoices = async () => {
      setLoading(true);
      const token = localStorage.getItem("portal_token");
      const config = { headers: { Authorization: `Bearer ${token}` } };
      try {
        const statusParam = filter !== "all" ? `&status=${filter}` : "";
        const res = await axios.get(`${API}/portal/invoices?limit=100${statusParam}`, config);
        setInvoices(res.data.invoices);
      } catch (error) {
        console.error("Error fetching invoices:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchInvoices();
  }, [filter]);

  const getTotalUnpaid = () => invoices.filter(i => ["pending", "sent", "overdue"].includes(i.status)).reduce((sum, i) => sum + (i.total || 0), 0);

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-[#0046BE]" /></div>;

  return (
    <div className="space-y-5 sm:space-y-6" data-testid="portal-invoices">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-800 font-['Outfit']">My Invoices</h1>
          {getTotalUnpaid() > 0 && (
            <p className="text-slate-400 text-sm mt-0.5">
              Outstanding: <span className="font-semibold text-red-500">${getTotalUnpaid().toFixed(2)}</span>
            </p>
          )}
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-none">
          {["all", "pending", "sent", "paid", "overdue"].map((status) => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              data-testid={`portal-invoice-filter-${status}`}
              className={`px-3.5 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all duration-200 ${
                filter === status
                  ? "bg-[#0046BE] text-white shadow-sm"
                  : "bg-white text-slate-400 hover:text-slate-600 border border-slate-100"
              }`}
            >
              {status === "all" ? "All" : status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {invoices.length === 0 ? (
        <div className="bg-white rounded-2xl py-16 text-center" style={{boxShadow: '0 1px 3px rgba(0,0,0,0.04)', border: '1px solid rgba(0,0,0,0.04)'}}>
          <FileText className="w-10 h-10 mx-auto text-slate-200 mb-3" />
          <p className="text-slate-300 text-sm">No invoices found</p>
        </div>
      ) : (
        <div className="space-y-2.5">
          {invoices.map((invoice) => (
            <Link key={invoice.invoice_id} to={`/portal/invoices/${invoice.invoice_id}`} data-testid={`portal-invoice-card-${invoice.invoice_id}`}>
              <div className="group bg-white rounded-2xl p-4 sm:p-5 transition-all duration-200 hover:translate-y-[-1px]" style={{boxShadow: '0 1px 3px rgba(0,0,0,0.04)', border: '1px solid rgba(0,0,0,0.04)'}}>
                <div className="flex items-center gap-3 sm:gap-4">
                  <div className="w-1 h-12 rounded-full flex-shrink-0" style={{background: getStatusColor(invoice.status)}} />
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                      <div>
                        <h3 className="text-sm sm:text-[15px] font-semibold text-slate-700">
                          Invoice #{invoice.invoice_number || invoice.invoice_id?.slice(0, 8)}
                        </h3>
                        <p className="text-xs text-slate-300 flex items-center gap-1.5 mt-0.5">
                          <Calendar className="w-3 h-3" />{formatDate(invoice.created_at)}
                          {invoice.due_date && (<><span className="text-slate-200 mx-1">|</span><Clock className="w-3 h-3" />Due: {formatDate(invoice.due_date)}</>)}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 sm:gap-3">
                        <span className="text-lg font-bold text-slate-800">${(invoice.total || 0).toFixed(2)}</span>
                        <Badge className="text-[10px] px-2 py-0.5 border" style={{background: `${getStatusColor(invoice.status)}08`, color: getStatusColor(invoice.status), borderColor: `${getStatusColor(invoice.status)}20`}}>
                          {invoice.status}
                        </Badge>
                        {invoice.status === "sent" && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#0046BE]/8 text-[#0046BE] border border-[#0046BE]/15 font-medium" data-testid={`pay-badge-${invoice.invoice_id}`}>
                            Pay Now
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

const PortalInvoiceDetails = () => {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { business } = usePortalAuth();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [paying, setPaying] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [tipAmount, setTipAmount] = useState(0);
  const [showTipUI, setShowTipUI] = useState(false);

  const getAuthConfig = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("portal_token")}` }
  });

  const fetchInvoice = async () => {
    try {
      const res = await axios.get(`${API}/portal/invoices/${invoiceId}`, getAuthConfig());
      setInvoice(res.data);
    } catch { /* ignore */ } finally { setLoading(false); }
  };

  useEffect(() => {
    fetchInvoice();
  }, [invoiceId]);

  // Handle payment callback from Stripe
  useEffect(() => {
    const paymentStatus = searchParams.get("payment");
    const sessionId = searchParams.get("session_id");

    if (paymentStatus === "success" && sessionId) {
      setCheckingPayment(true);
      const verifyPayment = async () => {
        try {
          const res = await axios.get(
            `${API}/portal/invoices/${invoiceId}/payment-status?session_id=${sessionId}`,
            getAuthConfig()
          );
          if (res.data.payment_status === "paid") {
            toast.success("Payment successful! Your invoice has been paid.");
            fetchInvoice(); // Refresh to show updated status
          } else {
            toast.info("Payment is being processed. Please check back shortly.");
          }
        } catch {
          toast.error("Could not verify payment status");
        } finally {
          setCheckingPayment(false);
          // Clean URL params
          setSearchParams({});
        }
      };
      verifyPayment();
    } else if (paymentStatus === "cancelled") {
      toast.info("Payment was cancelled");
      setSearchParams({});
    }
  }, [searchParams]);

  const handlePayNow = async () => {
    setPaying(true);
    try {
      const res = await axios.post(
        `${API}/portal/invoices/${invoiceId}/pay`,
        { origin_url: window.location.origin, tip_amount: Number(tipAmount) || 0 },
        getAuthConfig()
      );
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start payment");
      setPaying(false);
    }
  };

  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      const response = await axios.get(`${API}/portal/invoices/${invoiceId}/pdf`, { ...getAuthConfig(), responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `invoice_${invoice?.invoice_number || invoiceId.slice(0, 8)}.pdf`);
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Invoice downloaded!");
    } catch { toast.error("Failed to download invoice"); } finally { setDownloading(false); }
  };

  const formatDateFull = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" });
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-[#0046BE]" /></div>;

  if (!invoice) return (
    <div className="text-center py-16">
      <AlertCircle className="w-10 h-10 mx-auto text-slate-200 mb-3" />
      <p className="text-slate-400 text-sm">Invoice not found</p>
      <Button variant="outline" size="sm" onClick={() => navigate("/portal/invoices")} className="mt-4 rounded-xl">Back to Invoices</Button>
    </div>
  );

  return (
    <div className="space-y-5" data-testid="portal-invoice-details">
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={() => navigate("/portal/invoices")} className="text-slate-400 hover:text-slate-600 -ml-2 rounded-xl" data-testid="invoice-back-btn">
          <ArrowLeft className="w-4 h-4 mr-1.5" /> Back
        </Button>
        <div className="flex items-center gap-2">
          <Button onClick={handleDownloadPDF} disabled={downloading} size="sm" variant="outline" className="rounded-xl h-9 border-slate-200 text-slate-600" data-testid="invoice-download-btn">
            {downloading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Download className="w-4 h-4 mr-1.5" />}
            PDF
          </Button>
          {invoice && invoice.status === "sent" && (
            <>
              <Button onClick={() => setShowTipUI((v) => !v)} size="sm" variant="ghost" className="rounded-xl h-9 text-slate-500 hover:text-slate-700 text-xs" data-testid="invoice-tip-toggle">
                {showTipUI ? "Hide tip" : "Add tip"}
              </Button>
              <Button onClick={handlePayNow} disabled={paying} size="sm" className="bg-[#0046BE] hover:bg-[#0033A0] text-white rounded-xl h-9" data-testid="invoice-pay-btn">
                {paying ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <CreditCard className="w-4 h-4 mr-1.5" />}
                Pay {tipAmount > 0 ? `$${(Number(invoice.total || 0) + Number(tipAmount)).toFixed(2)}` : "Now"}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Tip selector */}
      {invoice && invoice.status === "sent" && showTipUI && (
        <div className="bg-white border border-slate-100 rounded-2xl p-4" data-testid="invoice-tip-section">
          <p className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">Add a tip</p>
          <div className="grid grid-cols-5 gap-2 mb-2">
            {[0, 5, 10, 15, 20].map((pct) => {
              const amt = pct === 0 ? 0 : Math.round((invoice.total || 0) * pct) / 100;
              const active = Number(tipAmount) === Number(amt);
              return (
                <button
                  key={pct}
                  type="button"
                  onClick={() => setTipAmount(amt)}
                  className={`py-2 rounded-lg text-xs font-semibold border transition-colors ${active ? "bg-[#0046BE] text-white border-[#0046BE]" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}
                  data-testid={`invoice-tip-${pct}`}
                >
                  {pct === 0 ? "None" : `${pct}%`}
                </button>
              );
            })}
          </div>
          <Input
            type="number"
            min="0"
            step="0.01"
            value={tipAmount || ""}
            onChange={(e) => setTipAmount(Math.max(0, Number(e.target.value) || 0))}
            placeholder="Custom tip ($)"
            className="h-9 text-sm rounded-lg"
            data-testid="invoice-tip-custom-input"
          />
        </div>
      )}

      {/* Payment checking banner */}
      {checkingPayment && (
        <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 flex items-center gap-3" data-testid="payment-checking-banner">
          <Loader2 className="w-5 h-5 animate-spin text-[#0046BE]" />
          <p className="text-sm text-blue-700">Verifying your payment...</p>
        </div>
      )}

      {/* Payment success banner */}
      {invoice && invoice.status === "paid" && invoice.payment_method === "stripe_connect" && (
        <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-4 flex items-center gap-3" data-testid="payment-success-banner">
          <CheckCircle className="w-5 h-5 text-emerald-500" />
          <div>
            <p className="text-sm font-medium text-emerald-700">Paid online</p>
            {invoice.paid_at && <p className="text-xs text-emerald-500">{formatDate(invoice.paid_at)}</p>}
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl overflow-hidden" style={{boxShadow: '0 1px 3px rgba(0,0,0,0.04)', border: '1px solid rgba(0,0,0,0.04)'}}>
        <div className="p-6 sm:p-8">
          {/* Invoice Header */}
          <div className="flex flex-col sm:flex-row justify-between gap-6 pb-6" style={{borderBottom: '1px solid rgba(0,0,0,0.04)'}}>
            <div>
              <h1 className="text-xl font-bold text-slate-800 font-['Outfit']">{business?.business_name || "Invoice"}</h1>
              {invoice.business?.business_address && <p className="text-slate-400 text-sm mt-1">{invoice.business.business_address}</p>}
              {invoice.business?.phone && <p className="text-slate-400 text-sm">{invoice.business.phone}</p>}
              {invoice.business?.email && <p className="text-slate-400 text-sm">{invoice.business.email}</p>}
            </div>
            <div className="sm:text-right">
              <h2 className="text-sm font-bold text-slate-400 uppercase tracking-wider">Invoice</h2>
              <p className="text-slate-600 font-semibold mt-0.5">#{invoice.invoice_number || invoice.invoice_id?.slice(0, 8)}</p>
              <Badge className="mt-2 text-xs px-2.5 py-0.5 border" style={{background: `${getStatusColor(invoice.status)}08`, color: getStatusColor(invoice.status), borderColor: `${getStatusColor(invoice.status)}20`}}>
                {invoice.status?.toUpperCase()}
              </Badge>
            </div>
          </div>

          {/* Dates */}
          <div className="grid sm:grid-cols-2 gap-4 py-5" style={{borderBottom: '1px solid rgba(0,0,0,0.04)'}}>
            <div><p className="text-xs text-slate-300 mb-0.5">Invoice Date</p><p className="text-sm font-medium text-slate-700">{formatDateFull(invoice.created_at)}</p></div>
            {invoice.due_date && (<div><p className="text-xs text-slate-300 mb-0.5">Due Date</p><p className="text-sm font-medium text-slate-700">{formatDateFull(invoice.due_date)}</p></div>)}
          </div>

          {/* Line Items */}
          <div className="py-5">
            <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-4">Items</h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr style={{borderBottom: '1px solid rgba(0,0,0,0.04)'}}>
                    <th className="text-left py-2.5 text-xs text-slate-300 font-medium">Description</th>
                    <th className="text-right py-2.5 text-xs text-slate-300 font-medium">Qty</th>
                    <th className="text-right py-2.5 text-xs text-slate-300 font-medium">Price</th>
                    <th className="text-right py-2.5 text-xs text-slate-300 font-medium">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {invoice.line_items && invoice.line_items.length > 0 ? (
                    invoice.line_items.map((item, idx) => (
                      <tr key={idx} style={{borderBottom: '1px solid rgba(0,0,0,0.03)'}}>
                        <td className="py-3 text-sm text-slate-700">{item.description || "Service"}</td>
                        <td className="py-3 text-sm text-slate-500 text-right">{item.quantity || 1}</td>
                        <td className="py-3 text-sm text-slate-500 text-right">${(item.unit_price || 0).toFixed(2)}</td>
                        <td className="py-3 text-sm text-slate-700 text-right font-medium">${((item.quantity || 1) * (item.unit_price || 0)).toFixed(2)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr style={{borderBottom: '1px solid rgba(0,0,0,0.03)'}}>
                      <td className="py-3 text-sm text-slate-700">{invoice.description || "Service"}</td>
                      <td className="py-3 text-sm text-slate-500 text-right">1</td>
                      <td className="py-3 text-sm text-slate-500 text-right">${(invoice.total || 0).toFixed(2)}</td>
                      <td className="py-3 text-sm text-slate-700 text-right font-medium">${(invoice.total || 0).toFixed(2)}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Totals */}
          <div className="flex justify-end pt-2">
            <div className="w-full sm:w-64 space-y-2">
              <div className="flex justify-between py-2"><span className="text-sm text-slate-400">Subtotal</span><span className="text-sm text-slate-700">${(invoice.subtotal || invoice.total || 0).toFixed(2)}</span></div>
              {invoice.tax > 0 && (<div className="flex justify-between py-2"><span className="text-sm text-slate-400">Tax</span><span className="text-sm text-slate-700">${(invoice.tax || 0).toFixed(2)}</span></div>)}
              <div className="flex justify-between py-3 font-bold text-lg" style={{borderTop: '2px solid rgba(0,0,0,0.06)'}}>
                <span className="text-slate-800">Total</span>
                <span className="text-slate-800">${(invoice.total || 0).toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Notes */}
          {invoice.notes && (
            <div className="mt-6 pt-5" style={{borderTop: '1px solid rgba(0,0,0,0.04)'}}>
              <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Notes</h3>
              <p className="text-sm text-slate-500">{invoice.notes}</p>
            </div>
          )}

          {/* Related Job */}
          {invoice.job && (
            <div className="mt-6 pt-5" style={{borderTop: '1px solid rgba(0,0,0,0.04)'}}>
              <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Related Job</h3>
              <Link to={`/portal/jobs/${invoice.job.job_id}`} className="inline-flex items-center gap-2 text-[#0046BE] hover:text-[#0033A0] text-sm font-medium transition-colors">
                <FileText className="w-4 h-4" />{invoice.job.title || `Job #${invoice.job.job_display_id || invoice.job.job_id?.slice(0, 8)}`}
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export { PortalInvoices, PortalInvoiceDetails };
